import { Star, StarBorder } from '@mui/icons-material';
import {
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Pagination,
  Stack,
  Switch,
  Typography
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import React, { useState } from 'react';
import { Pencil02, PlusCircle, Trash01 } from 'untitledui-js-base';
import { useCommonApi } from '../api';
import { useGetAutocompleteAddress } from '../hooks/useGetAutocompleteAddress';
import { formatPrice } from '../utils/formatPrice';
import { PageLayout } from './common';
import { NoDataFound } from './common/NoDataFound';
import { useAuth } from './hooks';
import { useRegistryFilter } from './hooks/useRegistryFilter';
import { RegistryFilters } from './registry/RegistryFilters';
import { RegistryRemoveDialog } from './registry/RegistryRemoveDialog';
import { TipperDialog, TipperFormValues } from './registry/TipperDialog';
import { useRegistry } from './registry/hook/useRegistry';
import { Role } from './types';
import { ACCIDENT_SOURCE, AccidentSourceValueItem, AccidentSourcesList } from './types/accident';

type DialogState = { mode: 'create' } | { mode: 'edit'; data: AccidentSourceValueItem };

const FAVOURITE_FILTER_KEY = 'tippers_favourite_filter';
const ASSIGNED_FILTER_KEY = 'tippers_assigned_filter';

interface TippersPageContentProps {
  tippers?: AccidentSourcesList;
  isLoading?: boolean;
  showFavouritesOnly: boolean;
  onFavouriteFilterChange: (value: boolean) => void;
  showAssignedOnly: boolean;
  onAssignedFilterChange: (value: boolean) => void;
}

const TippersPageContent = ({
  tippers,
  isLoading,
  showFavouritesOnly,
  onFavouriteFilterChange,
  showAssignedOnly,
  onAssignedFilterChange
}: TippersPageContentProps) => {
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const queryClient = useQueryClient();

  const { user } = useAuth();
  const {
    removeDialogItemId,
    setRemoveDialogItemId,
    isRemovingItem,
    handleRemoveRegistryItem,
    editItem,
    isEditingItem,
    createItem,
    isCreatingItem
  } = useRegistry();
  const { handleOnSearch, autocompleteItems, isLoadingData } = useGetAutocompleteAddress();
  const commonApi = useCommonApi();

  const addFavouriteMutation = useMutation({
    ...commonApi.addDealerSourceFavourite(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['common', 'accident', 'sources'] });
    }
  });

  const removeFavouriteMutation = useMutation({
    ...commonApi.removeDealerSourceFavourite(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['common', 'accident', 'sources'] });
    }
  });

  const handleToggleFavourite = (tipper: AccidentSourceValueItem) => {
    if (tipper.favourite) {
      removeFavouriteMutation.mutate({ sourceId: tipper.id });
    } else {
      addFavouriteMutation.mutate({ sourceId: tipper.id });
    }
  };

  const {
    items: tippersFilteredValues,
    pagination,
    filters
  } = useRegistryFilter(tippers?.values ?? [], {
    defaultPaginationState: { pageSize: 10, page: 1 }
  });

  const handleCreate = () => setDialog({ mode: 'create' });
  const handleEdit = (item: AccidentSourceValueItem) => setDialog({ mode: 'edit', data: item });
  const handleClose = () => setDialog(null);

  const handleSubmit = async (formData: TipperFormValues, typeChanged: boolean) => {
    if (!dialog) return;
    const type = formData.type ?? ACCIDENT_SOURCE.TIPPER;
    if (dialog.mode === 'edit') {
      editItem(
        {
          registryId: formData.id,
          registryType: type,
          data: {
            type,
            ...formData
          }
        },
        {
          onSuccess: () => {
            if (typeChanged) {
              queryClient.invalidateQueries({ queryKey: ['common', 'accident', 'sources'] });
            }
          }
        }
      );
    } else {
      createItem({
        registryType: ACCIDENT_SOURCE.TIPPER,
        data: {
          type: ACCIDENT_SOURCE.TIPPER,
          ...formData
        }
      });
    }
    handleClose();
  };

  return (
    <>
      <PageLayout
        title="Tipaři"
        extra={
          user?.role === Role.Dealer && (
            <Button
              variant="contained"
              color="success"
              startIcon={<PlusCircle size="20px" />}
              onClick={handleCreate}
              size="large"
            >
              Přidat nový záznam
            </Button>
          )
        }
      >
        {isLoading ? (
          <Stack width={1} alignItems="center">
            <CircularProgress color="primary" />
          </Stack>
        ) : (
          <Stack spacing={2} height={1}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <RegistryFilters filters={filters} disabled={isLoading} />
              <Stack direction="row">
                <FormControlLabel
                  control={
                    <Switch checked={showAssignedOnly} onChange={(e) => onAssignedFilterChange(e.target.checked)} />
                  }
                  label="Moji tipaři"
                />
                <FormControlLabel
                  control={
                    <Switch checked={showFavouritesOnly} onChange={(e) => onFavouriteFilterChange(e.target.checked)} />
                  }
                  label="Zobrazit pouze oblíbené"
                />
              </Stack>
            </Stack>
            <List sx={{ width: 1, bgcolor: 'background.paper', paddingX: 1 }}>
              {tippersFilteredValues.length ? (
                tippersFilteredValues?.map((tipper, idx) => (
                  <React.Fragment key={tipper.id}>
                    <ListItem
                      secondaryAction={
                        <Stack direction="row" gap={1}>
                          <IconButton size="small" aria-label="edit" color="info" onClick={() => handleEdit(tipper)}>
                            <Pencil02 />
                          </IconButton>
                          <IconButton
                            size="small"
                            aria-label="remove"
                            color="error"
                            onClick={() => setRemoveDialogItemId(tipper.id)}
                          >
                            <Trash01 />
                          </IconButton>
                        </Stack>
                      }
                      sx={{ paddingX: 1, paddingY: 1, width: 1 }}
                    >
                      <ListItemText
                        primary={
                          <Stack direction="row" alignItems="center" gap={0.5}>
                            <IconButton size="small" onClick={() => handleToggleFavourite(tipper)} sx={{ p: 0.5 }}>
                              {tipper.favourite ? (
                                <Star sx={{ color: 'warning.main' }} />
                              ) : (
                                <StarBorder sx={{ color: 'grey.400' }} />
                              )}
                            </IconButton>
                            <Stack>
                              <Typography fontWeight="600">{tipper.displayName ?? tipper.name}</Typography>
                              {tipper.tradeName && (
                                <Typography variant="body3" color="text.secondary">
                                  {tipper.name}
                                </Typography>
                              )}
                            </Stack>
                          </Stack>
                        }
                        secondary={
                          <Stack spacing={1} mt={1} component="span">
                            {tipper.birthDate && (
                              <Typography variant="body3">
                                Datum narození: {format(tipper.birthDate, 'dd.MM.yyyy')}
                              </Typography>
                            )}
                            <Typography variant="body3">{tipper.address}</Typography>
                            <Typography variant="body3" color={Boolean(tipper.commissionValue) ? 'success' : 'error'}>
                              Provize:{' '}
                              {`${tipper.commissionValue?.includes('%') ? tipper.commissionValue : formatPrice(Number.parseFloat(tipper.commissionValue ?? '0'))}`}
                            </Typography>
                          </Stack>
                        }
                      />
                    </ListItem>
                    {idx < tippersFilteredValues?.length - 1 && <Divider />}
                  </React.Fragment>
                ))
              ) : (
                <NoDataFound />
              )}
            </List>
            {pagination.totalPages > 1 && (
              <Pagination
                count={pagination.totalPages}
                page={pagination.page}
                variant="outlined"
                shape="rounded"
                onChange={(_, page) => pagination.setPage(page)}
              />
            )}
          </Stack>
        )}
      </PageLayout>

      <TipperDialog
        open={!!dialog}
        mode={dialog?.mode ?? 'create'}
        initialData={dialog && dialog.mode === 'edit' ? dialog.data : undefined}
        onClose={handleClose}
        onSubmit={handleSubmit}
        isLoading={isEditingItem || isCreatingItem}
        autocompleteResults={autocompleteItems}
        isLoadingAddressData={isLoadingData}
        onAutocompleteSearch={handleOnSearch}
      />
      {!!removeDialogItemId && (
        <RegistryRemoveDialog
          open={!!removeDialogItemId}
          id={removeDialogItemId}
          isLoading={isRemovingItem}
          onClose={() => setRemoveDialogItemId(null)}
          title="Opravdu chcete smazat tipaře?"
          onRemove={() => handleRemoveRegistryItem(ACCIDENT_SOURCE.TIPPER)}
        />
      )}
    </>
  );
};

export const TippersPage = () => {
  const { user } = useAuth();
  const [showFavouritesOnly, setShowFavouritesOnly] = useState(() => {
    const saved = localStorage.getItem(FAVOURITE_FILTER_KEY);
    return saved === 'true';
  });

  const handleFavouriteFilterChange = (value: boolean) => {
    setShowFavouritesOnly(value);
    localStorage.setItem(FAVOURITE_FILTER_KEY, String(value));
  };

  const [showAssignedOnly, setShowAssignedOnly] = useState(() => {
    const saved = localStorage.getItem(ASSIGNED_FILTER_KEY);
    return saved === 'true';
  });

  const handleAssignedFilterChange = (value: boolean) => {
    setShowAssignedOnly(value);
    localStorage.setItem(ASSIGNED_FILTER_KEY, String(value));
  };

  const { data: tippers, isLoading } = useQuery<AccidentSourcesList>({
    ...useCommonApi().getAccidentSourcesByType(
      ACCIDENT_SOURCE.TIPPER,
      showFavouritesOnly ? true : undefined,
      showAssignedOnly ? true : undefined
    ),
    enabled: user?.role === Role.Dealer
  });

  if (!user) return null;

  return (
    <TippersPageContent
      tippers={tippers}
      isLoading={isLoading}
      showFavouritesOnly={showFavouritesOnly}
      onFavouriteFilterChange={handleFavouriteFilterChange}
      showAssignedOnly={showAssignedOnly}
      onAssignedFilterChange={handleAssignedFilterChange}
    />
  );
};
